import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import {
  isChartSearchItem,
  isDocumentSearchItem,
  RecentItem,
  SearchableItem,
} from 'features/knowledgeDocumentViewer/types/searchableItems'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import {
  addRecentItem as addRecentItemToStorage,
  getRecentItems,
  removeRecentItem as removeRecentItemFromStorage,
  resolveRecentItemsForActiveDocuments,
} from 'features/knowledgeDocumentViewer/utils/recentItemsStorage'
import { useGeckoPermissions } from 'features/permissions'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { RolodexEntryType } from 'types'

interface UseRecentItemsReturn {
  recentItems: RecentItem[]
  addRecentItem: (item: SearchableItem) => void
}

export function useRecentItems(siteId?: string): UseRecentItemsReturn {
  const [recentItems, setRecentItems] = useState<RecentItem[]>([])
  const { user } = useGeckoPermissions()
  const { getPipelineConfigScopedTags, isPipelineConfigScopeLoading } =
    usePipelineConfig()

  const userId = user?.id
  const effectiveSiteId = siteId ?? ''

  const recentDocumentIds = useMemo(() => {
    return Array.from(
      new Set(
        recentItems
          .filter((recent) => isDocumentSearchItem(recent.item))
          .map((recent) => recent.item.id)
      )
    )
  }, [recentItems])

  const activeDocumentLoadOptions = useMemo(
    () =>
      !isPipelineConfigScopeLoading && recentDocumentIds.length > 0
        ? {
            type: [RolodexEntryType.SYSTEM_DOCUMENT],
            ids: recentDocumentIds,
            tags: getPipelineConfigScopedTags({ site: effectiveSiteId }),
            limit: recentDocumentIds.length,
          }
        : undefined,
    [
      effectiveSiteId,
      getPipelineConfigScopedTags,
      isPipelineConfigScopeLoading,
      recentDocumentIds,
    ]
  )

  const { data: activeDocuments } = useGetRolodexEntriesByLoadOptions<
    SystemDocumentEntry[]
  >(activeDocumentLoadOptions)

  // Verify recent CHARTS still exist. A Recent Chart card whose chart was
  // deleted opens a tab that can never resolve (error toast + empty chart),
  // so once the verification load reports back, prune any entry whose chart
  // id no longer has a rolodex entry — from storage and from live state.
  const recentChartIds = useMemo(() => {
    return Array.from(
      new Set(
        recentItems
          .filter((recent) => isChartSearchItem(recent.item))
          .map((recent) => recent.item.id)
      )
    )
  }, [recentItems])

  const chartLoadOptions = useMemo(
    () =>
      recentChartIds.length > 0
        ? {
            type: [RolodexEntryType.CHART],
            ids: recentChartIds,
            limit: recentChartIds.length,
          }
        : undefined,
    [recentChartIds]
  )

  const { data: chartEntryWrappers } =
    useGetRolodexEntriesByLoadOptions<Array<{ entry?: { id?: string } }>>(
      chartLoadOptions
    )

  useEffect(() => {
    if (!userId || !siteId || !chartEntryWrappers) return

    const existingChartIds = new Set(
      chartEntryWrappers
        .map((wrapper) => wrapper.entry?.id)
        .filter((id): id is string => Boolean(id))
    )
    const missingChartIds = recentChartIds.filter(
      (id) => !existingChartIds.has(id)
    )
    if (missingChartIds.length === 0) return

    const itemsBefore = getRecentItems(userId, siteId)
    let updatedItems = itemsBefore
    for (const id of missingChartIds) {
      updatedItems = removeRecentItemFromStorage(id, userId, siteId)
    }
    // Only publish an actual removal; a failed storage write returns the list
    // unchanged, and republishing it would re-trigger this effect forever.
    if (updatedItems.length !== itemsBefore.length) {
      setRecentItems(updatedItems)
    }
  }, [chartEntryWrappers, recentChartIds, userId, siteId])

  const resolvedRecentItems = useMemo(
    () =>
      resolveRecentItemsForActiveDocuments({
        recentItems,
        activeDocuments: activeDocuments ?? [],
      }),
    [activeDocuments, recentItems]
  )

  const handleAddRecentItem = useCallback(
    (item: SearchableItem) => {
      if (!userId || !siteId) return

      const updatedItems = addRecentItemToStorage(item, userId, siteId)
      setRecentItems(updatedItems)
    },
    [userId, siteId]
  )

  useEffect(() => {
    if (!userId || !siteId) {
      setRecentItems([])
      return
    }

    const loadItems = () => {
      setRecentItems(getRecentItems(userId, siteId))
    }

    loadItems()

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key?.startsWith(`gecko_recent_items_${userId}_${siteId}`)) {
        loadItems()
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [userId, siteId])

  return {
    recentItems: resolvedRecentItems,
    addRecentItem: handleAddRecentItem,
  }
}
